<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Icon from '@/components/Icon.vue'
import Avatar from '@/components/Avatar.vue'
import { useFloor } from '@/stores/floor'
import { usePresence } from '@/stores/presence'
import { useAdmin } from '@/stores/admin'
import { useSession } from '@/stores/session'
import { useMentions } from '@/stores/mentions'
import { useSuggestions } from '@/stores/suggestions'
import { useMeetings } from '@/stores/meetings'
import { MODES, modeMeta, type PresenceMode } from '@/types/atrium'

const route = useRoute()
const router = useRouter()
const floor = useFloor()
const presence = usePresence()
const admin = useAdmin()
const session = useSession()
const mentions = useMentions()
const suggestions = useSuggestions()
const meetings = useMeetings()

// Admin is shown to stewards/admins — and to everyone while no floor exists,
// because someone has to run setup (the token gates the actual call).
const showAdmin = computed(() => admin.isSteward || (floor.loaded && !floor.active))

const NAV: Array<{ name: string; label: string; icon: string }> = [
  { name: 'floor', label: 'Floor', icon: 'floor' },
  { name: 'tasks', label: 'Tasks', icon: 'kanban' },
  { name: 'mentions', label: 'Mentions', icon: 'at' },
  { name: 'calendar', label: 'Calendar', icon: 'cal2' },
  { name: 'memory', label: 'Memory', icon: 'memory' },
]

// Mentions + pending meeting suggestions + unseen meeting summaries share the
// rail badge — all three live under the Mentions view.
const mentionsUnread = computed(() => mentions.unread + suggestions.pendingCount + meetings.unread)

const me = computed(() => floor.you)
const myMode = computed(() => modeMeta(presence.myMode))
const modes = computed(() => Object.values(MODES))
const menuOpen = ref(false)
const nowDraft = ref(presence.myNow)

function go(name: string) {
  router.push({ name })
}
function pickMode(m: PresenceMode) {
  presence.setMode(m)
  menuOpen.value = false
}
function commitNow() {
  presence.setNow(nowDraft.value)
}
function toggleMenu() {
  nowDraft.value = presence.myNow
  menuOpen.value = !menuOpen.value
}
async function signOut() {
  menuOpen.value = false
  await session.logout()
  await router.replace({ name: 'login' })
}
</script>

<template>
  <aside class="rail">
    <button class="rail__brand" data-nav="floor" data-tip-pos="right" data-tip="Atrium" @click="go('floor')">
      <Icon name="logo" :size="22" />
    </button>
    <nav class="rail__nav">
      <button
        v-for="n in NAV"
        :key="n.name"
        class="rail__i"
        :class="{ 'is-active': route.name === n.name }"
        data-tip-pos="right"
        :data-tip="n.label"
        @click="go(n.name)"
      >
        <Icon :name="n.icon" :size="22" /><span>{{ n.label }}</span>
        <span v-if="n.name === 'mentions' && mentionsUnread" class="rail__badge">{{ mentionsUnread > 9 ? '9+' : mentionsUnread }}</span>
      </button>
    </nav>
    <div class="rail__spacer" />
    <button
      v-if="showAdmin"
      class="rail__i"
      :class="{ 'is-active': route.name === 'admin' }"
      data-tip-pos="right"
      data-tip="Admin"
      @click="go('admin')"
    >
      <Icon name="shield" :size="22" /><span>Admin</span>
    </button>
    <button
      class="rail__i"
      :class="{ 'is-active': route.name === 'settings' }"
      data-tip-pos="right"
      data-tip="Settings"
      @click="go('settings')"
    >
      <Icon name="settings" :size="22" /><span>Settings</span>
    </button>
    <button
      v-if="me"
      class="rail__me"
      data-tip-pos="right"
      :data-tip="`${me.name} · ${myMode.label}`"
      @click="toggleMenu"
    >
      <Avatar :person="me" :size="40" :ring="true" :tip="false" />
      <span class="rail__me-mode" :style="`background:${myMode.color}`" />
    </button>

    <!-- Presence switcher -->
    <div v-if="menuOpen" class="modal-back is-popover in" @mousedown.self="menuOpen = false">
      <div class="modal modal--pop presence-menu" role="dialog" style="position:fixed;left:88px;bottom:18px;width:300px">
        <div class="pm-head"><div class="eyebrow">Your presence</div><div class="txt-lo">Intentful, never binary.</div></div>
        <div class="pm-modes">
          <button
            v-for="m in modes"
            :key="m.id"
            class="pm-mode"
            :class="{ 'is-active': presence.myMode === m.id }"
            :style="`--c:${m.color}`"
            @click="pickMode(m.id)"
          >
            <span class="pm-dot" /><b>{{ m.label }}</b><span class="txt-lo">{{ m.hint }}</span>
          </button>
        </div>
        <label class="pm-now">
          <span class="eyebrow">The “now” line</span>
          <input v-model="nowDraft" placeholder="what you’re working on…" @change="commitNow" />
        </label>
        <div class="pm-foot">
          <button class="btn btn--ghost btn--sm" @click="signOut">
            <Icon name="leave" :size="14" /> Sign out
          </button>
        </div>
      </div>
    </div>
  </aside>
</template>
